import { ToastManagerRepository } from "@core/domain/repositories/toast-manager.repository";
import type { Nullable } from "@core/domain/types/nullable.type";
import { LoadLayoutStore } from "@core/presentation/components/feedback/load-layout/load-layout.store";
import type { Plot, PlotUpdate } from "@farm/domain/models/plot.model";
import { PlotUpdateUseCase } from "@farm/domain/usecases/plot-update.usecase";
import type { PlotModalFormValuesValidated } from "@farm/presentation/components/modal/plot/plot-modal-form.view";
import { BaseViewModel } from "@iot-platform/core";
import type { Station } from "@station/domain/models/station.model";
import { SpatialLevelGetAllUseCase } from "@station/domain/usecases/spatial-level-get-all.usecase";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class PlotUpdateModalViewModel extends BaseViewModel {
    @observable
    initialLoading = true;

    @observable
    stations: Readonly<Station>[] = [];

    @observable
    plot!: Readonly<Plot>;

    @observable
    farmId!: string;

    constructor(
        @inject(PlotUpdateUseCase)
        private readonly plotUpdateUseCase: PlotUpdateUseCase,
        @inject(SpatialLevelGetAllUseCase)
        private readonly spatialLevelGetAllUseCase: SpatialLevelGetAllUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override didMount(): void {
        this.initViewData();
        void this.fetchSpatialLevels();
    }

    @action
    initViewData(): void {
        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    async updatePlot(
        values: PlotModalFormValuesValidated,
    ): Promise<Nullable<Plot>> {
        LoadLayoutStore.start();

        const plotData: PlotUpdate = {
            id: Number(values.id.value),
            name: values.name,
            stationIds: values.stationIds,
            clayPercentage: values.clayPercentage,
            siltPercentage: values.siltPercentage,
            sandPercentage: values.sandPercentage,
        };

        const plotUpdated = await this.plotUpdateUseCase.execute(
            Number(this.farmId),
            plotData,
        );

        if (plotUpdated) {
            ToastManagerRepository.success();
        }

        LoadLayoutStore.finish();

        return plotUpdated;
    }

    @action
    setPlot(plot: Readonly<Plot>): void {
        runInAction(() => {
            this.plot = plot;
        });
    }

    @action
    setFarmId(farmId: string): void {
        runInAction(() => {
            this.farmId = farmId;
        });
    }

    @action
    private async fetchSpatialLevels(): Promise<void> {
        const spatialLevels = await this.spatialLevelGetAllUseCase.execute();
        runInAction(() => {
            this.stations = Array.from(spatialLevels.stationsMap.values());
        });
    }
}
